import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, PencilSimple, Trash, Phone, EnvelopeSimple, MapPin, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ElevatorCertificationCompanies() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/elevator-certification-companies`, { withCredentials: true });
      setCompanies(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchCompanies(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', phone: '', email: '', address: '', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (company) => {
    setEditing(company);
    setForm({ name: company.name, phone: company.phone || '', email: company.email || '', address: company.address || '', notes: company.notes || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/elevator-certification-companies/${editing.company_id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/elevator-certification-companies`, form, { withCredentials: true });
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      fetchCompanies();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/elevator-certification-companies/${deleting.company_id}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeleteOpen(false);
      fetchCompanies();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  return (
    <Layout>
      <div data-testid="elevator-certification-companies-page">
        <button
          onClick={() => navigate('/building-settings')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4"
          data-testid="cert-companies-back-to-settings"
        >
          <ArrowLeft size={14} />
          <span className="font-semibold">{t('settings') || 'Settings'}</span>
        </button>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('elevatorCertificationCompanies')}</h1>
          {isAdmin && (
            <Button onClick={openAdd} className="bg-zinc-950 rounded-none" data-testid="add-cert-company-btn">
              <Plus size={18} className="mr-2" />{t('addCertificationCompany')}
            </Button>
          )}
        </div>

        {companies.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500" data-testid="no-cert-companies">{t('noCertificationCompanies')}</div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cert-companies-table">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('companyName')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('phone')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('email')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('address')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('notes')}</th>
                    {isAdmin && <th className="text-right px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider w-24">{t('actions')}</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {companies.map(company => (
                    <tr key={company.company_id} className="hover:bg-zinc-50 transition-colors" data-testid={`cert-company-row-${company.company_id}`}>
                      <td className="px-5 py-3 font-semibold">{company.name}</td>
                      <td className="px-5 py-3 text-zinc-600">{company.phone || '-'}</td>
                      <td className="px-5 py-3 text-zinc-600">{company.email || '-'}</td>
                      <td className="px-5 py-3 text-zinc-600">{company.address || '-'}</td>
                      <td className="px-5 py-3 text-zinc-500 max-w-[200px] truncate">{company.notes || '-'}</td>
                      {isAdmin && (
                        <td className="px-5 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => openEdit(company)} className="p-1.5 hover:bg-zinc-100 transition-colors" data-testid={`edit-cert-company-${company.company_id}`}>
                              <PencilSimple size={16} />
                            </button>
                            <button onClick={() => { setDeleting(company); setDeleteOpen(true); }} className="p-1.5 hover:bg-red-50 text-red-600 transition-colors" data-testid={`delete-cert-company-${company.company_id}`}>
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{editing ? t('editCertificationCompany') : t('addCertificationCompany')}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>{t('companyName')} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-none" required data-testid="cert-company-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t('phone')}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-none" data-testid="cert-company-phone-input" /></div>
                <div><Label>{t('email')}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-none" data-testid="cert-company-email-input" /></div>
              </div>
              <div><Label>{t('address')}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-none" data-testid="cert-company-address-input" /></div>
              <div><Label>{t('notes')}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-none" rows={3} data-testid="cert-company-notes-input" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-cert-company-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>{t('deleteCertificationCompany')}</AlertDialogTitle><AlertDialogDescription>{t('confirmDeleteCertificationCompany')}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-cert-company">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
